import { httpsCallable, httpsCallableWithTimeout, functions } from '@/lib/functions';
import Constants from 'expo-constants';
import { mapCityPicksToRecommendations, requestCityPicks } from '@/services/cityPicks';

export class FunctionsCallError extends Error {
  code?: string;
  details?: unknown;
  constructor(message: string, opts?: { code?: string; details?: unknown }) {
    super(message);
    this.name = 'FunctionsCallError';
    this.code = opts?.code;
    this.details = opts?.details;
  }
}

type ChatPlannerPayload = {
  tripId: string;
  message: string;
  locale?: string;
  budget?: number;
  /** Priority / focus text; sent separately so it is not dropped when the main message is truncated. */
  intentAppendix?: string | null;
};

export type ChatPlannerSuggestion = {
  title: string;
  description: string;
  day: number;
  /** Primary city when the model returns multi-city suggestions. */
  city?: string;
  category?: string;
  isPackage?: boolean;
  address?: string;
  cost?: number;
  currency?: string;
  ticketLink?: string;
  needsTicket?: boolean;
};

export type ChatPlannerResult = {
  summary: string;
  suggestions: ChatPlannerSuggestion[];
  debug?: Record<string, unknown>;
};

const buildFunctionsHints = (error: any): string[] => {
  const code = error?.code as string | undefined;
  const message = error?.message || '';
  const hints: string[] = [];
  const emulatorHost =
    process.env.EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST ||
    (Constants.expoConfig?.extra as any)?.functionsEmulatorHost ||
    '192.168.x.x:5001';

  if (
    code === 'deadline-exceeded' ||
    code === 'functions/unavailable' ||
    /deadline|timed out|timeout/i.test(message)
  ) {
    hints.push(
      `Firebase Functions timed out. If you're on a physical device with emulators, set EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST=${emulatorHost} and restart Expo.`,
    );
    hints.push(
      'Confirm the Functions emulator is running (`firebase emulators:start --only functions`) and your device can reach port 5001 (same Wi‑Fi, firewall open).',
    );
  }

  if (/openai|api key|gpt|vertex/i.test(message) || code === 'functions/failed-precondition') {
    hints.push('Verify AI keys (e.g., OPENAI_API_KEY) are configured in the Functions environment or emulator .env and restart the emulator.');
  }

  if (code === 'functions/unauthenticated') {
    hints.push('Sign in again to continue.');
  }

  if (code === 'functions/resource-exhausted' && /plan ai idea cards/i.test(message)) {
    hints.push('Free plan includes a monthly limit of Plan AI idea cards. Upgrade to Premium for unlimited Plan AI, or try again next month.');
  }
  if (code === 'functions/resource-exhausted' && /travel co-pilot|travel companion/i.test(message)) {
    hints.push('Free plan includes a monthly limit of Travel Co-Pilot queries. Upgrade to Premium for unlimited assistance.');
  }
  if (code === 'functions/resource-exhausted' && /gap activity suggestions/i.test(message)) {
    hints.push('Free plan includes a monthly limit of AI gap suggestions. Upgrade to Premium for unlimited AI itinerary support.');
  }

  return hints;
};

// Keep a shared client timeout just under the longest backend AI callable timeout budget.
const CHAT_PLANNER_TIMEOUT_MS = 280000;
const AI_COPILOT_TIMEOUT_MS = 280000;
const callChatPlanner = httpsCallableWithTimeout<ChatPlannerPayload, ChatPlannerResult>(
  functions,
  'chatPlanner',
  CHAT_PLANNER_TIMEOUT_MS,
);

export const requestItinerarySuggestions = async (
  payload: ChatPlannerPayload,
): Promise<ChatPlannerResult> => {
  try {
    const response = await callChatPlanner(payload);
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[AI Planner] Error calling chatPlanner', { message });
    const hints = buildFunctionsHints(error);
    const base =
      error?.message ||
      'Unable to generate AI suggestions right now.';
    throw new FunctionsCallError([base, ...hints].join('\n\n'), {
      code: error?.code,
      details: error?.details,
    });
  }
};

type ApplySuggestionPayload = {
  tripId: string;
  itineraryItems: ChatPlannerSuggestion[];
};

type ApplySuggestionResponse = {
  appliedCount: number;
  suggestionDocId: string;
  itineraryItemIds: string[];
  status: 'applied';
};

const callApplySuggestion = httpsCallable<ApplySuggestionPayload, ApplySuggestionResponse>(
  functions,
  'applySuggestion',
);

export const applyItinerarySuggestions = async (
  payload: ApplySuggestionPayload,
): Promise<ApplySuggestionResponse> => {
  const response = await callApplySuggestion(payload);
  return response.data;
};

// Trip Recommendation Types
export type CostBreakdown = {
  transportation?: number;
  accommodation?: number;
  activities?: number;
  food?: number;
  other?: number;
};

export type PerTravelerCosts = {
  total: number;
  breakdown: CostBreakdown;
};

export type TripRecommendation = {
  destination: string;
  description: string;
  dates?: string;
  estimatedBudget?: number;
  whyThisMatch?: string;
  confidenceLabel?: 'high' | 'medium' | 'low';
  confidenceScore?: number;
  budgetFitScore?: number;
  bestMonths?: string[];
  riskFlags?: string[];
  suggestedStartDate?: string;
  suggestedEndDate?: string;
  duration?: number;
  costBreakdown?: CostBreakdown;
  perTravelerCosts?: PerTravelerCosts;
  numberOfTravelers?: number;
  itineraryHighlights?: string[];
  planVariant?: string;
};

type TripRecommendationRequest = {
  /** Deprecated: server uses `request.auth.uid` only; omit from new callers. */
  userId?: string;
  destination?: string;
  travelingFrom?: string;
  dateFlexibility: 'specific' | 'flexible' | 'unknown';
  startDate?: string;
  endDate?: string;
  lengthOfStay?: 'short' | '1week' | '2weeks' | 'long' | 'flexible';
  budgetRange: 'budget' | 'moderate' | 'luxury' | 'flexible';
  vibes: string[];
  numberOfTravelers?: number;
  visaFreeOnly?: boolean;
  citizenship?: string;
  travelersCitizenship?: string[];
  tripScope?: 'anywhere' | 'local' | 'international';
  travelCompanion?: 'solo' | 'partner' | 'friends' | 'family' | 'kids';
  dateWindow?: 'this_weekend' | 'this_month' | 'next_month' | 'flexible' | 'specific' | 'unknown';
  destinationIntent?: 'surprise' | 'known';
  freeTextPrompt?: string;
  inspirationMode?: string;
};

type TripRecommendationResponse = {
  recommendations: TripRecommendation[];
  debug?: Record<string, unknown>;
};

// Backend has timeoutSeconds: 120; use 130s client timeout for cold start / slow OpenAI.
const callTripRecommendations = httpsCallableWithTimeout<
  TripRecommendationRequest,
  TripRecommendationResponse
>(functions, 'tripRecommendations', 130000);

export const requestTripRecommendations = async (
  payload: TripRecommendationRequest,
): Promise<TripRecommendationResponse> => {
  try {
    const response = await callTripRecommendations(payload);
    return response.data;
  } catch (error: any) {
    const hints = buildFunctionsHints(error);
    const base = error?.message || 'Unable to generate destination recommendations right now.';
    throw new FunctionsCallError([base, ...hints].join('\n\n'), {
      code: error?.code,
      details: error?.details,
    });
  }
};

// Local picks recommendation types (Google Places-backed)
export type LocalPicksRecommendationItem = {
  id: string;
  type: 'poi' | 'activity' | 'flight' | 'hotel';
  category?: string;
  name: string;
  description?: string;
  location?: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  price?: {
    amount: number;
    currency: string;
  };
  rating?: number;
  imageUrl?: string;
  bookingLink?: string;
  metadata?: Record<string, unknown>;
};

type LocalPicksRecommendationRequest = {
  userId: string;
  destination: string;
  startDate?: string;
  endDate?: string;
  latitude?: number;
  longitude?: number;
  numberOfTravelers?: number;
  travelingFrom?: string;
};

type LocalPicksRecommendationResponse = {
  recommendations: LocalPicksRecommendationItem[];
  source: 'google' | 'cache' | 'fallback';
  destination: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

const buildFallbackRecommendations = (destination: string): LocalPicksRecommendationItem[] => {
  const picks = [
    {
      id: 'food',
      type: 'poi' as const,
      name: `Local eats in ${destination}`,
      description: 'Top-rated spots from our curated list of local favorites.',
    },
    {
      id: 'museum',
      type: 'poi' as const,
      name: `Museums & culture in ${destination}`,
      description: 'A mix of art, history, and science highlights.',
    },
    {
      id: 'outdoor',
      type: 'poi' as const,
      name: `Outdoor walks & viewpoints`,
      description: 'Scenic routes, parks, and viewpoints to stretch your legs.',
    },
    {
      id: 'daytrip',
      type: 'poi' as const,
      name: `Easy day trips from ${destination}`,
      description: 'Short hops nearby to add variety to your itinerary.',
    },
    {
      id: 'coffee',
      type: 'poi' as const,
      name: 'Coffee & cowork picks',
      description: 'Cafes with great vibes and Wi‑Fi for a quick break.',
    },
  ];
  return picks;
};

export const requestLocalPicksRecommendations = async (
  payload: LocalPicksRecommendationRequest,
): Promise<LocalPicksRecommendationResponse> => {
  try {
    const cityPicks = await requestCityPicks({
      city: payload.destination,
      latitude: payload.latitude,
      longitude: payload.longitude,
    });
    return {
      source: cityPicks.source,
      destination: payload.destination,
      coordinates:
        typeof payload.latitude === 'number' && typeof payload.longitude === 'number'
          ? { latitude: payload.latitude, longitude: payload.longitude }
          : undefined,
      recommendations: mapCityPicksToRecommendations(cityPicks.items, payload.destination),
    };
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Local Picks] Error calling getCityPicks', { message });
    return {
      source: 'fallback',
      destination: payload.destination,
      recommendations: buildFallbackRecommendations(payload.destination),
    };
  }
};

// Backwards-compatible aliases while UI code is migrated.
export type AmadeusRecommendationItem = LocalPicksRecommendationItem;
export const requestAmadeusRecommendations = requestLocalPicksRecommendations;

// Copilot helpers
type RebookingPreferences = {
  flexibility?: 'time' | 'date' | 'routing';
  budgetCap?: number;
  preferredAirlines?: string[];
  notes?: string;
};

export type RebookingOption = {
  title: string;
  description: string;
  estimatedCost?: number;
  currency?: string;
  changeImpact?: string[];
  providerLink?: string;
  riskLevel?: 'low' | 'medium' | 'high';
};

export type RebookingCopilotResponse = {
  summary: string;
  options: RebookingOption[];
  disclaimers?: string[];
};

type RebookingCopilotRequest = {
  tripId: string;
  bookingId: string;
  preferences?: RebookingPreferences;
};

const callRebookingCopilot = httpsCallableWithTimeout<
  RebookingCopilotRequest,
  RebookingCopilotResponse
>(functions, 'rebookingCopilot', AI_COPILOT_TIMEOUT_MS);

export const requestRebookingSupport = async (
  payload: RebookingCopilotRequest,
): Promise<RebookingCopilotResponse> => {
  try {
    const response = await callRebookingCopilot(payload);
    return response.data;
  } catch (error: any) {
    const hints = buildFunctionsHints(error);
    const base = error?.message || 'Unable to suggest rebooking options right now.';
    throw new Error([base, ...hints].join('\n\n'));
  }
};

export type DisruptionInsight = {
  risk: string;
  summary: string;
  recommendedActions: string[];
  priority: 'low' | 'medium' | 'high';
  contextualSuggestions?: Array<{
    action: string;
    description: string;
    type: 'find_nearby' | 'rebook' | 'wait' | 'alternative' | 'contact';
  }>;
};

export type DisruptionCopilotResponse = {
  alerts: DisruptionInsight[];
  monitoringTips?: string[];
};

type DisruptionCopilotRequest = {
  tripId: string;
  signals?: Array<{type: string; description: string}>;
};

const callDisruptionCopilot = httpsCallableWithTimeout<
  DisruptionCopilotRequest,
  DisruptionCopilotResponse
>(functions, 'disruptionCopilot', AI_COPILOT_TIMEOUT_MS);

export const requestDisruptionInsights = async (
  payload: DisruptionCopilotRequest,
): Promise<DisruptionCopilotResponse> => {
  try {
    const response = await callDisruptionCopilot(payload);
    return response.data;
  } catch (error: any) {
    const rawMessage = error?.message || 'Failed to analyze disruptions. Please try again.';
    const code = error?.code as string | undefined;
    const details = error?.details as unknown;
    const hints: string[] = [];

    if (
      /network request failed|unavailable|ecconnrefused|fetch failed|timeout/i.test(rawMessage) ||
      code === 'functions/unavailable'
    ) {
      hints.push(
        'If you are using the Firebase Functions emulator on a physical device, set `EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST` to your computer LAN IP (e.g. `192.168.x.x:5001`) and restart Expo.'
      );
    }
    if (/openai|api key|failed-precondition|permission-denied/i.test(rawMessage) || code === 'functions/failed-precondition') {
      hints.push('Ensure your Functions environment has the required AI keys (e.g. `OPENAI_API_KEY`) configured and the emulator is restarted.');
    }
    if (code === 'functions/unauthenticated') {
      hints.push('You appear to be signed out or missing permissions. Sign in again and retry.');
    }

    if (__DEV__) {
      hints.push(
        'If you are running locally: make sure the Functions emulator is actually running. Recent `firebase-tools` requires **Java 21+**; if the emulator fails to start due to Java, install JDK 21 and restart `firebase emulators:start --only functions`.'
      );
    }

    const debugBits: string[] = [];
    if (code) debugBits.push(`code: ${code}`);
    if (details) {
      try {
        debugBits.push(`details: ${typeof details === 'string' ? details : JSON.stringify(details)}`);
      } catch {
        debugBits.push('details: [unserializable]');
      }
    }

    const debugLine = debugBits.length ? `(${debugBits.join(' • ')})` : '';
    const extraHints = buildFunctionsHints(error);
    throw new Error([`${rawMessage}${debugLine ? `\n${debugLine}` : ''}`, ...hints, ...extraHints].join('\n\n'));
  }
};

export type BudgetCopilotResponse = {
  status: 'under' | 'on_track' | 'over' | 'unknown';
  summary: string;
  alerts: string[];
  recommendations: string[];
  forecast?: {
    projectedSpend: number;
    budgetRemaining: number;
    runwayDays?: number;
    currency?: string;
  };
};

type BudgetCopilotRequest = {
  tripId: string;
};

const callBudgetCopilot = httpsCallableWithTimeout<
  BudgetCopilotRequest,
  BudgetCopilotResponse
>(functions, 'budgetCopilot', AI_COPILOT_TIMEOUT_MS);

export const requestBudgetCopilot = async (
  payload: BudgetCopilotRequest,
): Promise<BudgetCopilotResponse> => {
  try {
    const response = await callBudgetCopilot(payload);
    return response.data;
  } catch (error: any) {
    const rawMessage = error?.message || 'Failed to analyze budget. Please try again.';
    const code = error?.code as string | undefined;
    const details = error?.details as unknown;
    const hints: string[] = [];

    if (
      /network request failed|unavailable|ecconnrefused|fetch failed|timeout/i.test(rawMessage) ||
      code === 'functions/unavailable'
    ) {
      hints.push(
        'If you are using the Firebase Functions emulator on a physical device, set `EXPO_PUBLIC_FUNCTIONS_EMULATOR_HOST` to your computer LAN IP (e.g. `192.168.x.x:5001`) and restart Expo.'
      );
    }
    if (/openai|api key|failed-precondition|permission-denied/i.test(rawMessage) || code === 'functions/failed-precondition') {
      hints.push('Ensure your Functions environment has the required AI keys (e.g. `OPENAI_API_KEY`) configured and the emulator is restarted.');
    }
    if (code === 'functions/unauthenticated') {
      hints.push('You appear to be signed out or missing permissions. Sign in again and retry.');
    }

    if (__DEV__) {
      hints.push(
        'If you are running locally: make sure the Functions emulator is actually running. Recent `firebase-tools` requires **Java 21+**; if the emulator fails to start due to Java, install JDK 21 and restart `firebase emulators:start --only functions`.'
      );
    }

    const debugBits: string[] = [];
    if (code) debugBits.push(`code: ${code}`);
    if (details) {
      try {
        debugBits.push(`details: ${typeof details === 'string' ? details : JSON.stringify(details)}`);
      } catch {
        debugBits.push('details: [unserializable]');
      }
    }

    const debugLine = debugBits.length ? `(${debugBits.join(' • ')})` : '';
    const extraHints = buildFunctionsHints(error);
    throw new Error([`${rawMessage}${debugLine ? `\n${debugLine}` : ''}`, ...hints, ...extraHints].join('\n\n'));
  }
};

// Gap Activity Suggestions Types
export type ReferencePoint = {
  type: 'activity' | 'accommodation';
  title: string;
  location: string;
  address?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
};

export type GapActivitySuggestion = {
  title: string;
  description: string;
  estimatedDuration: number; // in minutes
  location?: string;
  reason: string;
  walkingDistance?: string; // e.g., "10 minute walk"
};

type SuggestGapActivitiesRequest = {
  tripId: string;
  referencePoint: ReferencePoint;
  gap: {
    start: string; // ISO date string
    end: string; // ISO date string
    duration: number; // in minutes
    day: string; // YYYY-MM-DD
  };
  trip: {
    destination: string;
    budget?: number;
  };
  existingItems: Array<{
    title: string;
    location?: string;
    address?: string;
  }>;
};

type SuggestGapActivitiesResponse = {
  suggestions: GapActivitySuggestion[];
  debug?: Record<string, unknown>;
};

const callSuggestGapActivities = httpsCallableWithTimeout<SuggestGapActivitiesRequest, SuggestGapActivitiesResponse>(
  functions,
  'suggestGapActivities',
  65000,
);

export const requestGapActivitySuggestions = async (
  payload: SuggestGapActivitiesRequest,
): Promise<SuggestGapActivitiesResponse> => {
  try {
    const response = await callSuggestGapActivities(payload);
    return response.data;
  } catch (error: any) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[AI Planner] Error calling suggestGapActivities', { message });
    const hints = buildFunctionsHints(error);
    const base = error?.message || 'Unable to generate AI gap suggestions right now.';
    throw new FunctionsCallError([base, ...hints].join('\n\n'), {
      code: error?.code,
      details: error?.details,
    });
  }
};


